/**
 * ChatMessageMarkdown — renders a chat message's markdown content with
 * GFM (tables, strikethrough, task lists), math (inline + display),
 * styled inline citations, and styled handoff-package links.
 *
 * Mirrors the plugin wiring and citation handling of
 * components/workspace/ReportView.tsx, but scales typography down for
 * the chat sidebar's narrower measure. ReportView is for long-form
 * documents; this is for conversation density.
 *
 * Interactive link formats supported:
 *   1. `[label](#cite:marker_id)` — markdown link with #cite: scheme,
 *      rendered by the `a` component override as a citation chip.
 *   2. `[label]{ref:marker_id}` — the source form the backend emits.
 *      Rewritten to form #1 by `preprocessCitations` before parsing.
 *   3. `[label](ho_xxxxxx)` — markdown link with an `ho_` href,
 *      rendered as a handoff-package chip.
 *   4. Bare `ho_xxxxxx` (8-12 hex chars, not part of another word) —
 *      rewritten into `[ho_xxxxxx](ho_xxxxxx)` by
 *      `preprocessHandoffLinks` before parsing, then rendered as a
 *      handoff-package chip.
 *
 * Citation and handoff chips render as non-interactive spans unless the
 * caller supplies the `onCitationClick` / `onHandoffClick` callbacks —
 * in which case they render as real `<button>`s that dispatch those
 * callbacks. `GeminiChatArea` wires them to the corresponding
 * `window.handleReferenceClick` / `window.handleHandoffClick` globals
 * App.tsx registers.
 *
 * Special cards:
 *   Assistant messages containing a `### Research Proposal` block get
 *   that block wrapped in a visually distinct bordered card with a
 *   navy (--primary) left accent. (The accent was BARK-purple before
 *   palette-overhaul-2026-04; the corrective pass migrated it to navy
 *   to match the navy-first identity — see plan 002.) The card heading
 *   ("RESEARCH PROPOSAL") and optional subtitle ("— Large Exposure
 *   Analysis") are extracted from the markdown before react-markdown
 *   sees the content, so the heading doesn't also appear as a plain h3
 *   inside the card. The block ends at the next `### ` heading, a
 *   `---` horizontal rule, or end of message.
 *
 *   Assistant messages containing a `## Validated Answer` block get
 *   that block wrapped in a compact success-tinted card. The backend
 *   emits this heading when a report has been set, and the card makes
 *   that final answer easier to distinguish from ordinary chat.
 */

import React, { useMemo } from 'react';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import type { Components, UrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { BarChart3, CheckCircle2, FileText, Globe2 } from 'lucide-react';
import { isSafeUrl } from '../../utils/urlSafety';
import ConceptLink from './ConceptLink';
import { useAnswerConcepts, useAnswerArticles } from './AnswerConcepts';
import { remarkArticleLinks } from './articleLinks';
import { remarkConceptLinks } from './conceptLinks';

interface ChatMessageMarkdownProps {
    content: string;
    /** Optional — when provided, citation chips become clickable buttons that dispatch the marker id. */
    onCitationClick?: (markerId: string) => void;
    /** Optional — hover tooltip for a given marker. Return undefined for the default. */
    resolveCitationTooltip?: (markerId: string) => string | undefined;
    /** Optional — when provided, online-support chips ({online:online_id}) become clickable buttons that dispatch the online_support_id. */
    onOnlineRefClick?: (onlineSupportId: string) => void;
    /** Optional — hover tooltip for a given online_support_id. */
    resolveOnlineRefTooltip?: (onlineSupportId: string) => string | undefined;
    /** Optional — when provided, handoff-package chips become clickable buttons. */
    onHandoffClick?: (handoffId: string) => void;
    /** Optional — when provided, [label](report:id) links become clickable buttons dispatching the report id. */
    onReportClick?: (reportId: string) => void;
}

/**
 * Rewrite `[label]{ref:marker_id}` into `[label](#cite:marker_id)` so
 * ReactMarkdown can parse it as a standard link and our `a` override
 * can render it as a citation chip.
 *
 * Ported from components/workspace/ReportView.tsx so both surfaces
 * understand the same backend citation format.
 */
function preprocessCitations(content: string): string {
    return content.replace(
        /\[([^\]]+)\]\{ref:([^}]+)\}/g,
        '[$1](#cite:$2)',
    );
}

/**
 * Rewrite `[label]{online:online_id}` into `[label](#online:online_id)`
 * (BAR-39). Parallel to preprocessCitations but a distinct channel —
 * the `a` override renders these as OnlineSupport chips (globe icon,
 * neutral colour) rather than regulatory-citation chips (link blue).
 *
 * OnlineSupport is supporting material, not primary regulatory
 * evidence: different syntax, different render, different click
 * handler.
 */
function preprocessOnlineRefs(content: string): string {
    return content.replace(
        /\[([^\]]+)\]\{online:([^}]+)\}/g,
        '[$1](#online:$2)',
    );
}

/**
 * BAR-39: catch *bare* OnlineSupport references — `{online:online_id}`
 * with no preceding `[label]`. The orchestrator occasionally emits the
 * raw token without a label, which would otherwise leak into the
 * message as literal text like `{online:online_c3a2d5857c6d4fdd}`. Give
 * it a default label so it renders as a globe chip just like the
 * labelled form, making it instantly recognisable as an online
 * reference. Runs AFTER preprocessOnlineRefs (every `[label]{online:id}`
 * is already a link by then), so anything matching here is genuinely
 * label-less. Mirrors preprocessHandoffLinks' bare-`ho_` auto-linking.
 */
const BARE_ONLINE_LABEL = 'Online source';
function preprocessBareOnlineRefs(content: string): string {
    return content.replace(
        /(?<!\])\{online:([^}]+)\}/g,
        `[${BARE_ONLINE_LABEL}](#online:$1)`,
    );
}

/**
 * Preserve the one custom scheme handled by the component renderer. All
 * other URLs still pass through react-markdown's fail-closed transform.
 */
export const chatUrlTransform: UrlTransform = (value, key, node) => (
    key === 'href' && node.tagName === 'a' && value.startsWith('report:')
        ? value
        : defaultUrlTransform(value)
);

/**
 * Rewrite bare `ho_xxxxxxxx` handoff-package IDs (8–12 hex chars, not
 * already inside a word or an existing markdown link) into full
 * markdown links so react-markdown parses them and our `a` override
 * can render them as handoff chips.
 *
 * Ported from the old `formatChatMessage` regex in App.tsx so the
 * same auto-linking behaviour survives the switch to react-markdown.
 * Existing `[label](ho_xxx)` links don't need preprocessing — they
 * already match the `ho_` href branch in the `a` override.
 *
 * The negative lookbehind includes `(` so an `ho_` id sitting inside
 * the URL portion of an existing markdown link — `[label](ho_xxx)` —
 * is NOT rewritten. Without this, a message like
 *   Check [View](ho_abc12345) and ho_def98765
 * would become
 *   Check [View]([ho_abc12345](ho_abc12345)) and [ho_def98765](ho_def98765)
 * which breaks the first link.
 */
function preprocessHandoffLinks(content: string): string {
    return content.replace(
        /(?<!["\w/(])ho_([a-f0-9]{8,12})(?!["\w])/g,
        (_m, id) => `[ho_${id}](ho_${id})`,
    );
}

/**
 * The backend sometimes emits lists indented with 4+ leading spaces.
 * CommonMark treats any line with 4+ leading spaces after a blank line
 * as an indented code block — so a perfectly-formed numbered list like
 *
 *     1. First question
 *     2. Second question
 *
 * gets rendered as a monospace code block that overflows the chat's
 * narrow measure instead of a proper ordered list.
 *
 * This preprocessor strips exactly one 4-space indent from lines that
 * look like list markers (`\d+\.` / `-` / `*` / `+` followed by
 * whitespace). A line indented at 4 spaces becomes a top-level list
 * item; a line indented at 8 spaces becomes a 4-space-indented nested
 * item (CommonMark's nesting level) instead of being flattened. Fenced
 * code blocks (```...```) are untouched — they're delimited by fences,
 * not indentation.
 */
function dedentIndentedLists(content: string): string {
    // Split the content on triple-backtick code fences so the dedent only
    // applies to non-code sections. Capturing the delimiters means odd
    // indices in the resulting array are fenced-block segments, which we
    // pass through untouched. Without this, a fenced code block containing
    // `    1. step` would have its indentation stripped.
    const parts = content.split(/(```[\s\S]*?```)/g);
    return parts
        .map((part, i) => {
            if (i % 2 === 1) return part; // fenced block — leave it alone
            return part.replace(
                /^( {4,})(\d+\.\s|[-*+]\s)/gm,
                (_m, indent, marker) => indent.slice(4) + marker,
            );
        })
        .join('');
}

/**
 * The model now and then writes a bold lead-in with nothing after it:
 * `**3. Grunden för undantaget**Moderfinansiella …`. It happened where its
 * text slipped into tool-call markup, so the line break it meant to write
 * never arrived. Read as written, the lead-in runs into the next word.
 *
 * A line that opens with a bold run (after an optional list marker) followed
 * straight by a capital letter gets one space after the bold. Bold inside a
 * word (`**bank**en`), bold later in a line, and fenced code are untouched.
 */
function spaceAfterBoldLeadIns(content: string): string {
    return content
        .split(/(```[\s\S]*?```)/g)
        .map((part, i) => (i % 2 === 1 ? part : part.replace(
            /^([ \t]*(?:[-*+]\s+|\d+\.\s+)?\*\*[^*\n]+\*\*)(?=\p{Lu})/gmu,
            '$1 ',
        )))
        .join('');
}

type Chunk =
    | { kind: 'text'; content: string }
    | { kind: 'proposal'; content: string; subtitle?: string }
    | { kind: 'validated_answer'; content: string }
    | { kind: 'research_answer'; content: string };

/**
 * Split message content into plain chunks and Research Proposal chunks.
 *
 * A Research Proposal block:
 *   - starts with `### Research Proposal` (case-insensitive), optionally
 *     followed by ` — Subtitle` on the same line
 *   - ends at the next `### ` heading, a `---` horizontal rule, or EOF
 *
 * The heading line itself is consumed (not rendered as a plain h3
 * inside the card). The subtitle, if present, surfaces in the card
 * header next to "RESEARCH PROPOSAL".
 *
 * IMPORTANT: the regex literal stays declared inside this function on
 * purpose. It uses the `g` flag + stateful `exec()` via `lastIndex`,
 * which would misfire across calls if the regex were hoisted to
 * module scope and reused. Do not hoist it out.
 */
function splitOnResearchProposal(content: string): Chunk[] {
    const regex =
        /###\s+Research Proposal(?:\s*[—–-]+\s*([^\n]+))?\s*\n([\s\S]*?)(?=\n###\s|\n---|$)/gi;

    const chunks: Chunk[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(content)) !== null) {
        if (match.index > lastIndex) {
            chunks.push({ kind: 'text', content: content.slice(lastIndex, match.index) });
        }
        chunks.push({
            kind: 'proposal',
            subtitle: match[1]?.trim() || undefined,
            content: match[2].trim(),
        });
        lastIndex = match.index + match[0].length;
    }
    if (lastIndex < content.length) {
        chunks.push({ kind: 'text', content: content.slice(lastIndex) });
    }
    return chunks.length === 0 ? [{ kind: 'text', content }] : chunks;
}

/**
 * Split message content into plain chunks and Validated Answer chunks.
 *
 * A Validated Answer block:
 *   - starts with `## Validated Answer` (case-insensitive)
 *   - ends at the next `## ` heading, a `---` horizontal rule, or EOF
 *
 * The heading line is consumed so the card can render its own compact
 * label without duplicating the markdown h2.
 */
function splitOnValidatedAnswer(content: string): Chunk[] {
    // "Validated Answer" is emitted only after validate_work approved the
    // report in that turn; every other report-backed answer is a plain
    // "Research Answer" (checked by the automatic grounding tool, not reviewed).
    const regex = /^##\s+(Validated|Research) Answer\s*\n([\s\S]*?)(?=\n##\s|\n---|(?![\s\S]))/gim;

    const chunks: Chunk[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(content)) !== null) {
        if (match.index > lastIndex) {
            chunks.push({ kind: 'text', content: content.slice(lastIndex, match.index) });
        }
        chunks.push({
            kind: match[1].toLowerCase() === 'validated' ? 'validated_answer' : 'research_answer',
            content: match[2].trim(),
        });
        lastIndex = match.index + match[0].length;
    }
    if (lastIndex < content.length) {
        chunks.push({ kind: 'text', content: content.slice(lastIndex) });
    }
    return chunks.length === 0 ? [{ kind: 'text', content }] : chunks;
}

function splitMessageCards(content: string): Chunk[] {
    return splitOnValidatedAnswer(content).flatMap((chunk) => {
        if (chunk.kind !== 'text') return [chunk];
        return splitOnResearchProposal(chunk.content);
    });
}

function ResearchProposalCard({
    subtitle,
    children,
}: {
    subtitle?: string;
    children: React.ReactNode;
}) {
    return (
        // Borders: 1px default on top/right/bottom; 3px navy (--primary) on
        // the left. (Was BARK-purple pre-2026-04; migrated to navy in the
        // navy-first corrective pass.) Declared per-side to avoid the "set
        // all, override left" noise the previous shorthand produced.
        <div
            className="my-3 rounded-lg bg-bg-warm text-fg pl-5 pr-4 py-3
                       border-y border-r border-border-default
                       border-l-[3px] border-l-[color:var(--primary)]"
        >
            <div className="flex flex-wrap items-baseline gap-x-2 mb-2">
                <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[color:var(--primary)]">
                    Research proposal
                </span>
                {subtitle && (
                    <>
                        <span className="text-fg-tertiary text-[11px]">·</span>
                        <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[color:var(--primary)]">
                            {subtitle}
                        </span>
                    </>
                )}
            </div>
            <div>{children}</div>
        </div>
    );
}

function ValidatedAnswerCard({
    children,
    validated = true,
}: {
    children: React.ReactNode;
    validated?: boolean;
}) {
    const label = validated ? 'Validated answer' : 'Research answer';
    return (
        <section
            aria-label={label}
            data-testid={validated ? 'validated-answer-card' : 'research-answer-card'}
            className={`my-3 rounded-lg text-fg pl-4 pr-4 py-3
                       border-y border-r border-border-default shadow-bark-xs
                       border-l-[3px] ${validated ? 'bg-success-bg border-l-success' : 'bg-bg-warm border-l-border-strong'}`}
        >
            <div className="flex items-center gap-1.5 mb-2">
                {validated
                    ? <CheckCircle2 size={14} strokeWidth={1.8} className="text-success flex-shrink-0" />
                    : <FileText size={14} strokeWidth={1.8} className="text-fg-secondary flex-shrink-0" />}
                <span className={`text-[11px] font-semibold uppercase tracking-[0.08em] ${validated ? 'text-success' : 'text-fg-secondary'}`}>
                    {label}
                </span>
            </div>
            <div>{children}</div>
        </section>
    );
}

/**
 * Styling shared by citation and handoff chips. Per DESIGN.md: "Citation
 * chips (chip-citation) are always mono and blue — they signal a clickable
 * regulation reference." Pinned to --link / --link-bg directly so the
 * chips read as semantic citation-blue regardless of where --accent points.
 */
const CHIP_BASE =
    'inline-flex items-center px-1.5 py-0.5 mx-0.5 text-[11px] font-medium ' +
    'text-link bg-link-bg border border-transparent rounded align-baseline';

/**
 * Styling for OnlineSupport chips ({online:online_id}). Distinct from
 * the regulatory-citation CHIP_BASE: neutral colour, globe-icon prefix.
 * The visual difference is load-bearing — the reader needs to see at a
 * glance that this is supporting material, not a regulatory citation.
 */
const ONLINE_CHIP_BASE =
    'inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 text-[11px] font-medium ' +
    'text-fg-secondary bg-bg-warm border border-border-default rounded align-baseline';

/**
 * Allowlist check for external link hrefs. Only well-known navigation
 * schemes become real clickable links; anything else (javascript:,
 * data:, file:, vbscript: etc.) is rendered as plain text so an
 * attacker who could influence assistant output can't turn it into a
 * click-triggered XSS. Citation (`#cite:`) and handoff (`ho_`)
 * schemes are handled by their own branches in the `a` renderer and
 * never reach this check.
 *
 * Relative URLs (no scheme at all — `/path`, `#anchor`, `?q=1`) are
 * allowed; they can't escape the current origin.
 */
function isSafeExternalHref(href: string | undefined): boolean {
    if (!href) return false;
    const trimmed = href.trim().toLowerCase();
    // No scheme — relative URL, safe.
    if (!/^[a-z][a-z0-9+.-]*:/.test(trimmed)) return true;
    // http/https handled by the shared helper; mailto is also a safe
    // navigation scheme in chat content (online-support links use the
    // stricter http(s)-only `isSafeUrl` directly).
    return isSafeUrl(trimmed) || trimmed.startsWith('mailto:');
}

function buildComponents(
    onCitationClick?: (markerId: string) => void,
    resolveCitationTooltip?: (markerId: string) => string | undefined,
    onOnlineRefClick?: (onlineSupportId: string) => void,
    resolveOnlineRefTooltip?: (onlineSupportId: string) => string | undefined,
    onHandoffClick?: (handoffId: string) => void,
    onReportClick?: (reportId: string) => void,
): Components {
    return {
        a: ({ href, children, node }) => {
            // Citation chip — `[label](#cite:id)` or, after preprocessing, `[label]{ref:id}`.
            if (href?.startsWith('#cite:')) {
                const markerId = href.slice(6);
                const tooltip = resolveCitationTooltip?.(markerId) ?? 'Citation';
                if (onCitationClick) {
                    return (
                        <button
                            type="button"
                            onClick={() => onCitationClick(markerId)}
                            title={tooltip}
                            className={`${CHIP_BASE} hover:underline transition-colors cursor-pointer`}
                        >
                            {children}
                        </button>
                    );
                }
                return (
                    <span title={tooltip} className={CHIP_BASE}>
                        {children}
                    </span>
                );
            }

            // Online-support chip — `[label](#online:id)` or, after preprocessing, `[label]{online:id}`.
            // BAR-39: supporting material, not regulatory evidence. Distinct
            // visual treatment (globe icon, neutral colour) so a reader sees
            // immediately that this is not a regulation cite.
            if (href?.startsWith('#online:')) {
                const onlineId = href.slice(8);
                const tooltip = resolveOnlineRefTooltip?.(onlineId) ?? 'Online support (not a regulatory citation)';
                if (onOnlineRefClick) {
                    return (
                        <button
                            type="button"
                            onClick={() => onOnlineRefClick(onlineId)}
                            title={tooltip}
                            className={`${ONLINE_CHIP_BASE} hover:bg-surface-sunken transition-colors cursor-pointer`}
                        >
                            <Globe2 size={10} strokeWidth={1.5} className="flex-shrink-0" />
                            {children}
                        </button>
                    );
                }
                return (
                    <span title={tooltip} className={ONLINE_CHIP_BASE}>
                        <Globe2 size={10} strokeWidth={1.5} className="flex-shrink-0" />
                        {children}
                    </span>
                );
            }

            // Handoff-package chip — `[label](ho_xxxxxxxx)` or preprocessed bare `ho_xxxxxxxx`.
            if (href?.startsWith('ho_')) {
                const handoffId = href;
                if (onHandoffClick) {
                    return (
                        <button
                            type="button"
                            onClick={() => onHandoffClick(handoffId)}
                            title="Open handoff package"
                            className={`${CHIP_BASE} hover:underline transition-colors cursor-pointer`}
                        >
                            {children}
                        </button>
                    );
                }
                return (
                    <span title="Handoff package" className={CHIP_BASE}>
                        {children}
                    </span>
                );
            }

            // Report link — `[View Report: title](report:id)`. Clicking opens
            // the report via the provided callback. A Lucide report icon
            // preserves the previous visual cue without rendering emoji.
            if (href?.startsWith('report:')) {
                const reportId = href.slice(7);
                const linkClass =
                    'text-accent hover:text-accent-hover underline font-medium inline-flex items-baseline gap-1';
                if (onReportClick) {
                    return (
                        <button
                            type="button"
                            onClick={() => onReportClick(reportId)}
                            title="Open report"
                            className={`${linkClass} cursor-pointer`}
                        >
                            <BarChart3 size={16} strokeWidth={1.5} aria-hidden="true" />
                            <span>{children}</span>
                        </button>
                    );
                }
                // No handler wired — render as a disabled-looking chip so
                // users see "this was a link" instead of nothing.
                return (
                    <span
                        title="Report link (handler not registered)"
                        className="text-fg-tertiary underline decoration-dotted italic inline-flex items-baseline gap-1"
                    >
                        <BarChart3 size={16} strokeWidth={1.5} aria-hidden="true" />
                        <span>{children}</span>
                    </span>
                );
            }

            // Regular external link — only render as clickable if the
            // scheme is in our allowlist (http/https/mailto or relative).
            // Anything else (javascript:, data:, file:, vbscript:, …) is
            // rendered as plain text to eliminate click-triggered XSS.
            if (!isSafeExternalHref(href)) {
                return <span>{children}</span>;
            }
            return (
                <ConceptLink href={href!} concept={node?.properties?.['data-concept'] === 'true'}>
                    {children}
                </ConceptLink>
            );
        },
        // Headings — chat density: sidebar-scaled, tighter vertical rhythm than ReportView.
        // Text color intentionally NOT set: lets the parent bubble's color cascade,
        // so these render white inside the user's own teal bubble and dark inside
        // the assistant's light bubble.
        h1: ({ children }) => (
            <h1 className="text-sm font-bold mt-3 mb-1.5 first:mt-0">{children}</h1>
        ),
        h2: ({ children }) => (
            <h2 className="text-sm font-semibold mt-3 mb-1.5 first:mt-0">{children}</h2>
        ),
        h3: ({ children }) => (
            <h3 className="text-xs font-semibold uppercase tracking-wide mt-3 mb-1 first:mt-0 opacity-80">
                {children}
            </h3>
        ),
        // Body copy — color cascades from parent bubble, see note on headings.
        p: ({ children }) => (
            <p className="text-sm leading-relaxed mb-2 last:mb-0">{children}</p>
        ),
        // Lists — color cascades from parent bubble. Item gaps use a child
        // selector rather than space-y-*, so .prose-chat (index.css) can set them.
        ul: ({ children }) => (
            <ul className="list-disc list-outside pl-4 mb-2 [&>li+li]:mt-0.5 text-sm leading-snug">
                {children}
            </ul>
        ),
        ol: ({ children, start }) => (
            <ol start={start} className="list-decimal list-outside pl-4 mb-2 [&>li+li]:mt-0.5 text-sm leading-snug">
                {children}
            </ol>
        ),
        li: ({ children }) => <li className="text-sm leading-snug">{children}</li>,
        // Tables — GFM via remark-gfm
        table: ({ children }) => (
            <div className="overflow-x-auto my-2">
                <table className="border-collapse border border-border-default text-xs w-full">
                    {children}
                </table>
            </div>
        ),
        // `style` carries the column's GFM alignment (`---:`), so keep it.
        th: ({ children, style }) => (
            <th style={style} className="border border-border-default px-2 py-1 bg-bg-warm font-semibold text-left text-fg-secondary">
                {children}
            </th>
        ),
        td: ({ children, style }) => (
            <td style={style} className="border border-border-default px-2 py-1 text-fg align-top">
                {children}
            </td>
        ),
        // Blockquote — italic + slight opacity instead of text-fg-secondary so
        // the muted feel works on both light and dark bubble backgrounds.
        blockquote: ({ children }) => (
            <blockquote className="border-l-[3px] border-border-default pl-3 my-2 italic opacity-80">
                {children}
            </blockquote>
        ),
        // Code — single style for inline. Block rendering is handled by `pre`,
        // which resets the inner `<code>`'s own chip styling so the block
        // doesn't render as a chip-inside-a-box. This avoids the fragile
        // `className?.includes('language-')` detection that missed fenced
        // blocks without a language tag.
        code: ({ children }) => (
            <code className="text-xs font-mono bg-bg-warm text-fg px-1 py-0.5 rounded">
                {children}
            </code>
        ),
        pre: ({ children }) => (
            // whitespace-pre-wrap + break-words: long lines wrap within the
            // chat's narrow measure instead of overflowing horizontally.
            // [&_code]:* arbitrary-variant utilities strip the inner
            // `<code>`'s chip styling so a fenced block renders as one
            // clean container, not a chip nested in a box.
            <pre
                className="bg-bg-warm border border-border-default rounded-md p-2 my-2 text-xs
                           whitespace-pre-wrap break-words
                           [&_code]:bg-transparent [&_code]:p-0 [&_code]:mx-0 [&_code]:rounded-none"
            >
                {children}
            </pre>
        ),
        // Inline emphasis — color cascades from parent bubble.
        strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
        em: ({ children }) => <em className="italic">{children}</em>,
        // Separator
        hr: () => <hr className="border-border-default my-3" />,
    };
}

function ChatMessageMarkdown({
    content,
    onCitationClick,
    resolveCitationTooltip,
    onOnlineRefClick,
    resolveOnlineRefTooltip,
    onHandoffClick,
    onReportClick,
}: ChatMessageMarkdownProps) {
    const concepts = useAnswerConcepts(content);
    const conceptPlugin = useMemo(() => concepts ? remarkConceptLinks(concepts) : null, [concepts]);
    const articles = useAnswerArticles(content);
    const articlePlugin = useMemo(() => articles ? remarkArticleLinks(articles) : null, [articles]);
    const processed = useMemo(
        () =>
            spaceAfterBoldLeadIns(dedentIndentedLists(
                preprocessHandoffLinks(
                    preprocessBareOnlineRefs(
                        preprocessOnlineRefs(
                            preprocessCitations(content ?? ''),
                        ),
                    ),
                ),
            )),
        [content],
    );
    const chunks = useMemo(() => splitMessageCards(processed), [processed]);

    const components = useMemo(
        () => buildComponents(
            onCitationClick,
            resolveCitationTooltip,
            onOnlineRefClick,
            resolveOnlineRefTooltip,
            onHandoffClick,
            onReportClick,
        ),
        [onCitationClick, resolveCitationTooltip, onOnlineRefClick, resolveOnlineRefTooltip, onHandoffClick, onReportClick],
    );

    if (!processed) return null;

    return (
        <>
            {chunks.map((chunk, i) => {
                if (!chunk.content) return null;
                const markdown = (
                    <ReactMarkdown
                        remarkPlugins={[remarkGfm, remarkMath, ...(articlePlugin ? [articlePlugin] : []), ...(conceptPlugin ? [conceptPlugin] : [])]}
                        rehypePlugins={[rehypeKatex]}
                        components={components}
                        urlTransform={chatUrlTransform}
                    >
                        {chunk.content}
                    </ReactMarkdown>
                );
                if (chunk.kind === 'proposal') {
                    return (
                        <ResearchProposalCard key={i} subtitle={chunk.subtitle}>
                            {markdown}
                        </ResearchProposalCard>
                    );
                }
                if (chunk.kind === 'validated_answer' || chunk.kind === 'research_answer') {
                    return (
                        <ValidatedAnswerCard key={i} validated={chunk.kind === 'validated_answer'}>
                            {markdown}
                        </ValidatedAnswerCard>
                    );
                }
                return <React.Fragment key={i}>{markdown}</React.Fragment>;
            })}
        </>
    );
}

// Memoized — streaming messages re-render on every token; skip re-parse
// when content is unchanged. Props only include primitives and stable
// callbacks, so default shallow compare is sufficient.
export default React.memo(ChatMessageMarkdown);
